import type { V2 } from '../core/math';
import type { SimEvent } from '../sim/events';
import type { Shape } from '../sim/shapes';
import type { Element, EntityId } from '../sim/types';
import type { PlayOptions } from './AudioEngine';
import type { SfxId } from './sfx';

/**
 * Maps simulation events to positioned sound cues. `mapSimEvent` is pure
 * (given the per-context state) so the table is unit-testable;
 * `handleSimEvent` plays the cues on an engine; `bindSimAudio` wraps both
 * plus the per-frame listener update and optional footsteps.
 *
 * Presentation-only: nothing here feeds back into the simulation.
 */

/** What the bindings need to know about the world. */
export interface SimAudioContext {
  /** Ground position of an entity (null when unknown / despawned). */
  pos(id: EntityId): V2 | null | undefined;
  isLocalPlayer(id: EntityId): boolean;
  isMonster(id: EntityId): boolean;
  isBoss(id: EntityId): boolean;
  /** MonsterDef.creature ('Beast', 'Human', 'Undead', 'Construct', ...): picks voices and impact materials. */
  creature?(id: EntityId): string | undefined;
  /** Registry lookup of a skill's element (flavours casts, projectiles and eruptions). */
  skillElement?(skillId: string): Element | undefined;
  /** SkillDef.kind ('basic' | 'dash' | 'awakening' | ...). */
  skillKind?(skillId: string): string | undefined;
  /** Listener position for `bindSimAudio().frame()` (usually the local hero). */
  listener?(): V2 | null | undefined;
  /** Camera yaw (CameraRig.yaw) for panning. */
  listenerYaw?(): number;
  /** Ground surface under a point: enables local footsteps in `frame()`. */
  surfaceAt?(x: number, z: number): Surface | undefined;
  /** Randomness source (tests pass a deterministic one). */
  random?(): number;
}

export type Surface = 'grass' | 'stone' | 'sand' | 'snow' | 'wood' | 'water';

/** A sound to play: an sfx id plus play options, and an optional music duck in dB. */
export interface SfxCue extends PlayOptions {
  id: SfxId;
  duck?: number;
}

/** The subset of AudioEngine the bindings use (easy to fake in tests). */
export interface SfxPlayer {
  play(id: SfxId, opts?: PlayOptions): unknown;
  duck(db?: number, hold?: number, release?: number): void;
  setListener(x: number, z: number, yaw?: number): void;
}

type Who = 'local' | 'player' | 'boss' | 'monster' | 'other';

interface AreaInfo {
  owner: EntityId;
  friendly: boolean;
  skillId?: string;
}

/** Per-context memory: which skill an actor is casting, who owns areas/projectiles. */
export interface SimAudioState {
  lastSkill: Map<EntityId, string>;
  areas: Map<number, AreaInfo>;
  projectiles: Map<number, AreaInfo>;
}

const states = new WeakMap<SimAudioContext, SimAudioState>();

export function createSimAudioState(): SimAudioState {
  return { lastSkill: new Map(), areas: new Map(), projectiles: new Map() };
}

function stateFor(ctx: SimAudioContext): SimAudioState {
  let s = states.get(ctx);
  if (!s) {
    s = createSimAudioState();
    states.set(ctx, s);
  }
  return s;
}

/** Keeps bookkeeping maps bounded even if end events are missed. */
function remember<K, V>(map: Map<K, V>, key: K, value: V, cap = 512): void {
  map.set(key, value);
  if (map.size > cap) {
    const first = map.keys().next();
    if (!first.done) map.delete(first.value);
  }
}

export const CAST_SFX: Record<Element, SfxId | null> = {
  none: null,
  fire: 'cast_fire',
  water: 'cast_ice',
  lightning: 'cast_lightning',
  earth: 'cast_earth',
  holy: 'cast_holy',
  dark: 'cast_dark',
};

/** Matches any of `words` at the start of a token ("fire_rain", "blue_ice"), not inside words ("slice"). */
const tok = (words: string): RegExp => new RegExp(`(?:^|[^a-z])(?:${words})`);

/** Recognised projectile/hit `vfx` keywords (first match wins). */
const VFX_SFX: readonly (readonly [RegExp, SfxId, string?])[] = [
  [tok('shotgun|pellet|scatter|buckshot'), 'shotgun'],
  [tok('rifle|snipe|sniper|railgun'), 'rifle'],
  [tok('pistol|bullet|revolver|gun'), 'pistol'],
  [tok('arrow|bow|dart|quarrel'), 'arrow'],
  [tok('shockwave|slam|quake'), 'ground_slam'],
  [tok('lightning|thunder|storm|spark|shock|volt|zap'), 'cast_lightning'],
  [tok('fire|flame|ember|burn|inferno|meteor|magma|blaze'), 'cast_fire'],
  [tok('ice|frost|snow|glacial|freeze|cryo'), 'cast_ice'],
  [tok('water|wave|tide|aqua|bubble'), 'cast_ice', 'water'],
  [tok('holy|radiant|sacred|divine|light|sun'), 'cast_holy'],
  [tok('dark|shadow|void|hollow|abyss|curse'), 'cast_dark'],
  [tok('earth|rock|stone|boulder|sand'), 'cast_earth'],
  [tok('arcane|magic|orb|mana|star|bolt|missile'), 'cast_arcane'],
];
const GUNS: ReadonlySet<SfxId> = new Set<SfxId>(['shotgun', 'rifle', 'pistol', 'arrow']);

export function sfxForVfx(vfx: string | undefined): { id: SfxId; variant?: string } | null {
  if (!vfx) return null;
  const v = vfx.toLowerCase();
  for (const [re, id, variant] of VFX_SFX)
    if (re.test(v)) return variant ? { id, variant } : { id };
  return null;
}

/** Impact material for a creature type. */
export function materialFor(creature: string | undefined): { id: SfxId; pitch: number } {
  const c = (creature ?? '').toLowerCase();
  if (/crystal|core|glass/.test(c)) return { id: 'impact_metal', pitch: 1.4 };
  if (/knight|armou?r|machine|mech|metal|construct/.test(c))
    return { id: 'impact_metal', pitch: 1 };
  if (/golem|stone|rock|elemental|statue/.test(c)) return { id: 'impact_stone', pitch: 0.9 };
  if (/undead|skeleton|bone/.test(c)) return { id: 'impact_stone', pitch: 1.35 };
  return { id: 'impact_flesh', pitch: 1 };
}

/** Rough size of a shape in metres. */
export function shapeSize(shape: Shape | undefined): number {
  if (!shape) return 0;
  switch (shape.kind) {
    case 'circle':
    case 'cone':
      return shape.radius;
    case 'ring':
      return shape.outer;
    case 'rect':
      return Math.max(shape.length / 2, shape.width / 2);
  }
}

const VOLUME: Record<Who, number> = { local: 1, player: 0.55, boss: 1, monster: 0.7, other: 0.6 };

/** Maps one sim event to sound cues. Mutates only the bookkeeping `state`. */
export function mapSimEvent(
  ev: SimEvent,
  ctx: SimAudioContext,
  state: SimAudioState = stateFor(ctx),
): SfxCue[] {
  const rnd = (): number => (ctx.random ? ctx.random() : Math.random());
  const who = (id: EntityId | null | undefined): Who => {
    if (id === null || id === undefined) return 'other';
    if (ctx.isLocalPlayer(id)) return 'local';
    if (ctx.isBoss(id)) return 'boss';
    if (ctx.isMonster(id)) return 'monster';
    return id > 0 ? 'player' : 'other';
  };
  const at = (id: EntityId | null | undefined): { x?: number; z?: number } => {
    if (id === null || id === undefined) return {};
    const p = ctx.pos(id);
    return p ? { x: p.x, z: p.z } : {};
  };
  const atV = (p: V2): { x: number; z: number } => ({ x: p.x, z: p.z });
  const elementOf = (skillId: string | undefined): Element | undefined => {
    if (!skillId) return undefined;
    const e = ctx.skillElement?.(skillId);
    return e && e !== 'none' ? e : undefined;
  };
  const castCue = (el: Element): { id: SfxId; variant?: string } => {
    const id = CAST_SFX[el] ?? 'cast_arcane';
    return el === 'water' ? { id, variant: 'water' } : { id };
  };
  const creature = (id: EntityId): string | undefined => ctx.creature?.(id)?.toLowerCase();

  switch (ev.type) {
    case 'hit': {
      const onLocal = ctx.isLocalPlayer(ev.tgtId);
      const byLocal = ctx.isLocalPlayer(ev.srcId);
      const near = onLocal || byLocal;
      if (ev.heal)
        return ev.amount > 0 ? [{ id: 'heal', ...at(ev.tgtId), volume: near ? 0.7 : 0.35 }] : [];
      if (ev.shield) return [{ id: 'shield', ...at(ev.tgtId), volume: near ? 0.7 : 0.35 }];
      if (ev.dot)
        return onLocal && ev.amount > 0
          ? [{ id: 'impact_flesh', ...at(ev.tgtId), volume: 0.2, pitch: 1.2 }]
          : [];
      if (ev.amount <= 0 && ev.absorbed <= 0) return [];
      const vol = byLocal ? 1 : onLocal ? 0.85 : 0.5;
      let mat: { id: SfxId; pitch: number };
      if (ev.absorbed > 0 && ev.absorbed >= ev.amount) mat = { id: 'impact_metal', pitch: 1.35 };
      else if (onLocal) mat = { id: 'impact_flesh', pitch: 0.95 };
      else mat = materialFor(ctx.creature?.(ev.tgtId));
      const pitch = ctx.isBoss(ev.tgtId) ? mat.pitch * 0.8 : mat.pitch;
      const cues: SfxCue[] = [
        {
          id: mat.id,
          ...at(ev.tgtId),
          volume: vol,
          pitch,
          ...(ev.element !== 'none' ? { variant: ev.element } : {}),
        },
      ];
      if (ev.crit) cues.push({ id: 'crit_hit', ...at(ev.tgtId), volume: byLocal ? 0.9 : 0.45 });
      return cues;
    }

    case 'action': {
      remember(state.lastSkill, ev.actorId, ev.skillId);
      const w = who(ev.actorId);
      const v = VOLUME[w];
      const o = atV(ev.origin);
      const kind = ctx.skillKind?.(ev.skillId);
      switch (ev.kind) {
        case 'hit': {
          const cues: SfxCue[] = [];
          const byVfx = sfxForVfx(ev.vfx);
          const size = shapeSize(ev.shape);
          const round = ev.shape?.kind === 'circle' || ev.shape?.kind === 'ring';
          const heavy = ev.vfx === 'heavy';
          if (byVfx && GUNS.has(byVfx.id)) {
            cues.push({ id: byVfx.id, ...o, volume: v });
          } else if (w === 'boss' || w === 'monster') {
            const big = w === 'boss';
            if (round && size >= 3)
              cues.push({ id: 'ground_slam', ...o, volume: v, pitch: big ? 0.7 : 0.9 });
            else cues.push({ id: 'swing_heavy', ...o, volume: v * 0.8, pitch: big ? 0.65 : 0.85 });
          } else if (round && (size >= 3 || heavy)) {
            cues.push({ id: 'ground_slam', ...o, volume: v * 0.85 });
          } else if (heavy || size >= 4 || ev.shape?.kind === 'rect') {
            cues.push({ id: 'swing_heavy', ...o, volume: v * 0.9 });
          } else {
            cues.push({ id: 'swing_light', ...o, volume: v * (kind === 'basic' ? 0.75 : 0.9) });
          }
          const el = ev.element && ev.element !== 'none' ? ev.element : undefined;
          if (el) cues.push({ ...castCue(el), ...o, volume: v * 0.5 });
          else if (byVfx && !GUNS.has(byVfx.id)) cues.push({ ...byVfx, ...o, volume: v * 0.5 });
          if (kind === 'awakening') {
            cues.push({ id: 'explosion', ...o, volume: v * 0.8, pitch: 0.8 });
            if (w === 'local') cues[0] = { ...cues[0]!, duck: -3 };
          }
          return cues;
        }
        case 'move':
          return kind === 'dash' ? [] : [{ id: 'dash', ...o, volume: v * 0.5 }];
        case 'teleport':
          return [{ id: 'blink', ...o, volume: v * 0.9 }];
        case 'self':
          return [{ id: 'buff', ...o, volume: v * 0.8 }];
        case 'party':
          return [{ id: 'buff', ...o, volume: v * 0.9, variant: 'party' }];
        default:
          return [];
      }
    }

    case 'castStage': {
      remember(state.lastSkill, ev.actorId, ev.skillId);
      const w = who(ev.actorId);
      const o = at(ev.actorId);
      const variant = creature(ev.actorId);
      if (ev.anim === 'Roar') {
        return [
          {
            id: w === 'boss' ? 'boss_roar' : 'monster_roar',
            ...o,
            volume: w === 'boss' ? 1 : 0.8,
            ...(variant ? { variant } : {}),
          },
        ];
      }
      if (ev.phase !== 'stage') {
        if (ev.duration < 0.25) return [];
        const vol = w === 'local' ? 0.7 : w === 'boss' ? 0.8 : 0.4;
        return [{ id: 'charge_up', ...o, duration: Math.min(2.5, ev.duration), volume: vol }];
      }
      if (
        (w === 'monster' || w === 'boss') &&
        ev.stage === 0 &&
        /^(Attack|Slam|Charge|Breath|Cast)/.test(ev.anim) &&
        rnd() < (w === 'boss' ? 0.3 : 0.18)
      ) {
        return [
          {
            id: 'monster_growl',
            ...o,
            volume: 0.6,
            pitch: w === 'boss' ? 0.6 : 1,
            ...(variant ? { variant } : {}),
          },
        ];
      }
      return [];
    }

    case 'projectile': {
      const skillId = state.lastSkill.get(ev.ownerId);
      remember(state.projectiles, ev.id, { owner: ev.ownerId, friendly: false, skillId });
      const w = who(ev.ownerId);
      const v = VOLUME[w];
      const o = atV(ev.pos);
      const byVfx = sfxForVfx(ev.vfx);
      if (byVfx) return [{ ...byVfx, ...o, volume: v * (GUNS.has(byVfx.id) ? 1 : 0.6) }];
      const el = elementOf(skillId);
      if (el) return [{ ...castCue(el), ...o, volume: v * 0.6 }];
      if ((w === 'monster' || w === 'boss') && ev.speed >= 17)
        return [{ id: 'arrow', ...o, volume: v }];
      return [{ id: 'cast_arcane', ...o, volume: v * 0.5 }];
    }

    case 'projectileEnd':
      state.projectiles.delete(ev.id);
      return [];

    case 'area': {
      const skillId = state.lastSkill.get(ev.ownerId);
      remember(state.areas, ev.id, { owner: ev.ownerId, friendly: ev.friendly, skillId });
      const w = who(ev.ownerId);
      const o = atV(ev.origin);
      if (ev.friendly) return [];
      if ((w === 'monster' || w === 'boss') && ev.telegraph !== 'none' && ev.delay >= 0.35) {
        if (w === 'boss' || shapeSize(ev.shape) >= 5)
          return [
            {
              id: 'telegraph_warning',
              ...o,
              duration: ev.delay,
              volume: w === 'boss' ? 0.75 : 0.45,
            },
          ];
        return [];
      }
      if ((w === 'local' || w === 'player') && ev.delay >= 0.2) {
        const el = elementOf(skillId);
        const byVfx = sfxForVfx(ev.vfx);
        const cue = el
          ? castCue(el)
          : byVfx && !GUNS.has(byVfx.id)
            ? byVfx
            : { id: 'cast_arcane' as SfxId };
        return [{ ...cue, ...o, volume: VOLUME[w] * 0.6 }];
      }
      return [];
    }

    case 'areaErupt': {
      const info = state.areas.get(ev.id) ?? state.projectiles.get(ev.id);
      if (info?.friendly) return [];
      const w = info ? who(info.owner) : 'other';
      const v = VOLUME[w];
      const o = atV(ev.origin);
      const size = shapeSize(ev.shape);
      const el =
        elementOf(info?.skillId) ?? (sfxForVfx(ev.vfx)?.id === 'cast_fire' ? 'fire' : undefined);
      const variant = el ? { variant: el } : {};
      const fromProjectile = !state.areas.has(ev.id) && state.projectiles.has(ev.id);
      if (ev.vfx === 'meteor' || fromProjectile) {
        return [
          {
            id: 'explosion',
            ...o,
            volume: v * Math.min(1, Math.max(0.4, size / 4)),
            pitch: size < 2.5 ? 1.25 : 1,
            ...variant,
          },
        ];
      }
      if (ev.vfx === 'pillar') {
        return [
          { id: 'ground_slam', ...o, volume: v * 0.8, ...variant },
          { ...(el ? castCue(el) : { id: 'cast_holy' }), ...o, volume: v * 0.6 },
        ];
      }
      if (size >= 3)
        return [
          {
            id: 'ground_slam',
            ...o,
            volume: v * (w === 'boss' ? 1 : 0.8),
            pitch: w === 'boss' ? 0.75 : w === 'monster' ? 0.9 : 1,
            ...variant,
          },
        ];
      return [{ id: 'impact_stone', ...o, volume: v * 0.5, ...variant }];
    }

    case 'areaEnd':
      state.areas.delete(ev.id);
      return [];

    case 'counter':
      return [{ id: 'counter', ...at(ev.tgtId), volume: 1, duck: -4 }];

    case 'counterWindow':
      return [{ id: 'counter_cue', ...at(ev.id), volume: 0.9 }];

    case 'staggerBreak':
      return [{ id: 'stagger_break', ...at(ev.id), volume: 1, duck: -4 }];

    case 'partBreak':
      return [{ id: 'part_break', ...at(ev.id), volume: 1 }];

    case 'death': {
      const w = who(ev.id);
      const o = at(ev.id);
      if (w === 'local') return [{ id: 'death', volume: 1, duck: -8 }];
      if (w === 'player') return [{ id: 'death', ...o, volume: 0.6, variant: 'ally' }];
      if (w === 'boss') {
        return [
          { id: 'boss_roar', ...o, volume: 1, variant: 'death' },
          { id: 'ground_slam', ...o, volume: 0.9, pitch: 0.6, delay: 1.4 },
        ];
      }
      const variant = creature(ev.id);
      return [{ id: 'monster_death', ...o, volume: 0.7, ...(variant ? { variant } : {}) }];
    }

    case 'dash':
      return [
        { id: 'dash', ...at(ev.actorId), volume: ctx.isLocalPlayer(ev.actorId) ? 0.85 : 0.5 },
      ];

    case 'standUp':
      return [{ id: 'dash', ...at(ev.actorId), volume: 0.5, pitch: 1.2 }];

    case 'reaction': {
      const local = ctx.isLocalPlayer(ev.tgtId);
      if (ev.kind === 'launch')
        return [{ id: 'jump_land', ...at(ev.tgtId), delay: 0.85, volume: local ? 0.8 : 0.35 }];
      if (ev.kind === 'knockdown')
        return [
          { id: 'jump_land', ...at(ev.tgtId), delay: 0.2, volume: local ? 0.7 : 0.3, pitch: 0.9 },
        ];
      return [];
    }

    case 'announce':
      switch (ev.style) {
        case 'boss':
          return [{ id: 'announce_boss', volume: 1, duck: -4 }];
        case 'warning':
          return [{ id: 'alert', volume: 0.9 }];
        case 'mechanic':
          return [{ id: 'alert', volume: 0.8, variant: 'mechanic' }];
        case 'success':
          return [{ id: 'quest_complete', volume: 0.8 }];
        default:
          return [{ id: 'bell_chime', volume: 0.35 }];
      }

    case 'bossPhase':
      return [{ id: 'boss_roar', ...at(ev.id), volume: 0.9 }];

    case 'identity':
      return ev.op === 'perfect' && ctx.isLocalPlayer(ev.actorId)
        ? [{ id: 'perfect', volume: 0.9 }]
        : [];

    case 'revive':
      return [{ id: 'revive', ...at(ev.id), volume: ctx.isLocalPlayer(ev.id) ? 1 : 0.5 }];

    case 'despawn':
      state.lastSkill.delete(ev.id);
      return [];

    default:
      return [];
  }
}

/** Plays the cues for one event. */
export function handleSimEvent(engine: SfxPlayer, ev: SimEvent, ctx: SimAudioContext): void {
  for (const cue of mapSimEvent(ev, ctx)) {
    const { id, duck, ...opts } = cue;
    engine.play(id, opts);
    if (duck !== undefined) engine.duck(duck);
  }
}

export interface SimAudioBinding {
  /** Feed the events drained from `World` this frame. */
  handle(events: readonly SimEvent[]): void;
  /** Once per rendered frame: moves the listener and plays local footsteps. */
  frame(dt: number): void;
  /** Forget per-zone bookkeeping (call on zone change). */
  reset(): void;
}

const STRIDE = 1.7;
const FOOTSTEP: Record<Surface, { id: SfxId; pitch: number; volume: number }> = {
  grass: { id: 'footstep_grass', pitch: 1, volume: 0.35 },
  sand: { id: 'footstep_grass', pitch: 0.8, volume: 0.3 },
  snow: { id: 'footstep_grass', pitch: 0.65, volume: 0.35 },
  stone: { id: 'footstep_stone', pitch: 1, volume: 0.3 },
  wood: { id: 'footstep_stone', pitch: 0.75, volume: 0.32 },
  water: { id: 'water_splash', pitch: 1.6, volume: 0.12 },
};

/**
 * Convenience wrapper: `const sfx = bindSimAudio(audio, ctx)`; then
 * `sfx.handle(world.drainEvents())` and `sfx.frame(dt)` every frame.
 */
export function bindSimAudio(engine: SfxPlayer, ctx: SimAudioContext): SimAudioBinding {
  let last: V2 | null = null;
  let walked = 0;
  return {
    handle(events) {
      for (const ev of events) handleSimEvent(engine, ev, ctx);
    },
    frame() {
      const p = ctx.listener?.();
      if (!p) return;
      engine.setListener(p.x, p.z, ctx.listenerYaw?.());
      if (!ctx.surfaceAt) return;
      if (last) {
        const d = Math.hypot(p.x - last.x, p.z - last.z);
        // Teleports and zone loads don't count as walking.
        walked = d > 3 ? 0 : walked + d;
        if (walked >= STRIDE) {
          walked -= STRIDE;
          const surface = ctx.surfaceAt(p.x, p.z);
          const f = surface ? FOOTSTEP[surface] : undefined;
          if (f) engine.play(f.id, { volume: f.volume, pitch: f.pitch });
        }
      }
      last = { x: p.x, z: p.z };
    },
    reset() {
      const s = stateFor(ctx);
      s.lastSkill.clear();
      s.areas.clear();
      s.projectiles.clear();
      last = null;
      walked = 0;
    },
  };
}

// ------------------------------------------------------------------ UI helpers

export type UiSound =
  | 'click'
  | 'hover'
  | 'open'
  | 'close'
  | 'error'
  | 'accept'
  | 'complete'
  | 'coin'
  | 'pickup'
  | 'levelUp'
  | 'honeStrike'
  | 'honeSuccess'
  | 'honeFail'
  | 'waystone'
  | 'portal';

export const UI_SFX: Record<UiSound, SfxId> = {
  click: 'ui_click',
  hover: 'ui_hover',
  open: 'ui_open',
  close: 'ui_close',
  error: 'ui_error',
  accept: 'quest_accept',
  complete: 'quest_complete',
  coin: 'coin',
  pickup: 'pickup',
  levelUp: 'level_up',
  honeStrike: 'hone_strike',
  honeSuccess: 'hone_success',
  honeFail: 'hone_fail',
  waystone: 'waystone',
  portal: 'portal',
};

/** Plays a UI sound (non-positional). */
export function uiSound(engine: Pick<SfxPlayer, 'play'>, kind: UiSound, volume = 1): void {
  engine.play(UI_SFX[kind], { volume });
}

/** Loot drop sound for an item rarity name. */
export function lootSfx(rarity: string): SfxId {
  switch (rarity.toLowerCase()) {
    case 'legendary':
    case 'relic':
    case 'ancient':
    case 'mythic':
      return 'loot_legendary';
    case 'epic':
      return 'loot_epic';
    case 'rare':
      return 'loot_rare';
    default:
      return 'loot_common';
  }
}
